// Resources System - 资源管理系统
// 统一管理游戏中的所有资源、材料、配方和效果

// 资源类型定义
export const RESOURCE_TYPES = {
  HERB: 'herb',           // 草药
  MINERAL: 'mineral',     // 矿物
  BEAST_PART: 'beast_part', // 妖兽材料
  SPIRITUAL: 'spiritual', // 灵物
  PILL: 'pill',           // 丹药
  ARTIFACT: 'artifact'    // 法宝成品
};

// 统一的物品配置系统
// 包含物品基础信息、使用效果、配方等所有数据
export const ITEMS = {
  // 草药类 - 基础材料，可直接使用或用于炼丹
  '灵草': {
    type: RESOURCE_TYPES.HERB,
    name: '灵草',
    description: '普通的灵草，吸收天地灵气而生，可入药炼丹，恢复少许气血',
    rarity: 1,
    value: 10,
    usable: true,
    useEffect: {
      type: 'heal',
      amount: 30
    }
  },

  '九叶灵芝': {
    type: RESOURCE_TYPES.HERB,
    name: '九叶灵芝',
    description: '生长九片叶子的珍贵灵芝，千年一遇，药效惊人，可续命回春',
    rarity: 4,
    value: 500,
    usable: false
  },

  '紫灵花': {
    type: RESOURCE_TYPES.HERB,
    name: '紫灵花',
    description: '紫色灵花，吸收紫气东来之精华，含有浓郁灵力，用于炼制灵符',
    rarity: 2,
    value: 50,
    usable: false
  },

  '血参': {
    type: RESOURCE_TYPES.HERB,
    name: '血参',
    description: '生长在鲜血土壤中的珍贵药材，治疗内外伤势，补血养气',
    rarity: 3,
    value: 200,
    usable: false
  },

  '雪莲': {
    type: RESOURCE_TYPES.HERB,
    name: '雪莲',
    description: '生长在万年积雪中的圣药，纯净无暇，可洗髓伐毛，提升修炼资质',
    rarity: 4,
    value: 400,
    usable: false
  },

  // 矿物类 - 基础材料，用于炼器
  '木材': {
    type: RESOURCE_TYPES.MINERAL,
    name: '木材',
    description: '普通的木材，吸收天地精华，可用于炼制法器或建造洞府',
    rarity: 1,
    value: 20,
    usable: false
  },

  '灵石': {
    type: RESOURCE_TYPES.MINERAL,
    name: '灵石',
    description: '蕴含浓郁灵气的石头，是修士修炼的必备资源',
    rarity: 2,
    value: 100,
    usable: false
  },

  '上品灵石': {
    type: RESOURCE_TYPES.MINERAL,
    name: '上品灵石',
    description: '品质上乘的灵石，灵气纯净无比，修炼时事半功倍',
    rarity: 3,
    value: 300,
    usable: false
  },

  '玄铁矿': {
    type: RESOURCE_TYPES.MINERAL,
    name: '玄铁矿',
    description: '玄黑色的铁矿，坚硬无比，是炼制法宝的上佳材料',
    rarity: 3,
    value: 250,
    usable: false
  },

  '紫金矿': {
    type: RESOURCE_TYPES.MINERAL,
    name: '紫金矿',
    description: '稀有的紫金矿石，蕴含天地紫气，是炼制仙器的珍贵材料',
    rarity: 4,
    value: 600,
    usable: false
  },

  // 妖兽材料 - 战斗掉落，用于炼器或制药
  '狼皮': {
    type: RESOURCE_TYPES.BEAST_PART,
    name: '狼皮',
    description: '灰狼的皮毛，柔韧而有弹性，可用于炼制护甲',
    rarity: 1,
    value: 30,
    usable: false
  },

  '狼牙': {
    type: RESOURCE_TYPES.BEAST_PART,
    name: '狼牙',
    description: '锋利的狼牙，蕴含野性力量，可镶嵌在武器上增强攻击',
    rarity: 2,
    value: 80,
    usable: false
  },

  '虎皮': {
    type: RESOURCE_TYPES.BEAST_PART,
    name: '虎皮',
    description: '猛虎的皮毛，坚韧无比，是炼制上品护甲的材料',
    rarity: 2,
    value: 120,
    usable: false
  },

  '虎骨': {
    type: RESOURCE_TYPES.BEAST_PART,
    name: '虎骨',
    description: '虎骨，强筋健骨，入药可增强体质，炼器可提升法宝威力',
    rarity: 3,
    value: 300,
    usable: false
  },

  '蛇胆': {
    type: RESOURCE_TYPES.BEAST_PART,
    name: '蛇胆',
    description: '蛇胆，明目清热，入药可解毒疗伤，修炼时可增强目力',
    rarity: 2,
    value: 150,
    usable: false
  },

  '龙鳞': {
    type: RESOURCE_TYPES.BEAST_PART,
    name: '龙鳞',
    description: '真龙的鳞片，坚不可摧，蕴含龙族血脉之力，是炼制仙器的顶级材料',
    rarity: 5,
    value: 1000,
    usable: false
  },

  '凤羽': {
    type: RESOURCE_TYPES.BEAST_PART,
    name: '凤羽',
    description: '凤凰的羽毛，蕴含涅槃重生之力，可炼制飞行法宝或疗伤圣药',
    rarity: 5,
    value: 1200,
    usable: false
  },

  // 灵物类 - 天地精华，用于高级炼制
  '云露': {
    type: RESOURCE_TYPES.SPIRITUAL,
    name: '云露',
    description: '云海中凝聚的灵露，纯净无比，可净化心神，提升悟性',
    rarity: 3,
    value: 180,
    usable: false
  },

  '火精': {
    type: RESOURCE_TYPES.SPIRITUAL,
    name: '火精',
    description: '天地间纯净的火元素结晶，蕴含毁灭与重生之力',
    rarity: 3,
    value: 220,
    usable: false
  },

  '雷晶': {
    type: RESOURCE_TYPES.SPIRITUAL,
    name: '雷晶',
    description: '雷电精华凝聚而成的晶石，拥有天罚之威，可炼制雷系法宝',
    rarity: 4,
    value: 350,
    usable: false
  },

  // 成品类 - 可使用或装备的物品

  // 丹药 - 可使用的消耗品
  '聚气丹': {
    type: RESOURCE_TYPES.PILL,
    name: '聚气丹',
    description: '辅助修炼的丹药，可以加速灵气吸收，提升修炼速度',
    rarity: 2,
    value: 150,
    usable: true,
    useEffect: {
      type: 'buff',
      buffName: '聚气加速',
      duration: 3600,
      effects: { cultivationSpeed: 20 }
    },
    recipe: {
      skill: '炼丹术',
      ingredients: [
        { item: '灵草', count: 3 },
        { item: '灵石', count: 1 }
      ],
      difficulty: 1,
      successRate: 0.8
    }
  },

  '疗伤丹': {
    type: RESOURCE_TYPES.PILL,
    name: '疗伤丹',
    description: '治疗伤势的丹药，可以快速恢复气血',
    rarity: 2,
    value: 120,
    usable: true,
    useEffect: {
      type: 'heal',
      amount: 50
    },
    recipe: {
      skill: '炼丹术',
      ingredients: [
        { item: '血参', count: 2 },
        { item: '灵草', count: 2 }
      ],
      difficulty: 2,
      successRate: 0.7
    }
  },

  '洗髓丹': {
    type: RESOURCE_TYPES.PILL,
    name: '洗髓丹',
    description: '改善体质的丹药，提高修炼资质和悟性',
    rarity: 3,
    value: 400,
    usable: true,
    useEffect: {
      type: 'attribute',
      attributes: { cultivationSpeed: 10, maxQi: 20 }
    },
    recipe: {
      skill: '炼丹术',
      ingredients: [
        { item: '雪莲', count: 1 },
        { item: '九叶灵芝', count: 1 },
        { item: '上品灵石', count: 2 }
      ],
      difficulty: 4,
      successRate: 0.5
    }
  },

  '续脉丹': {
    type: RESOURCE_TYPES.PILL,
    name: '续脉丹',
    description: '修复经脉受损的丹药，恢复战斗损伤的经脉',
    rarity: 4,
    value: 800,
    usable: true,
    useEffect: {
      type: 'heal_injury',
      amount: 100,
      removeDebuff: '经脉受损'
    },
    recipe: {
      skill: '炼丹术',
      ingredients: [
        { item: '虎骨', count: 1 },
        { item: '九叶灵芝', count: 2 },
        { item: '龙鳞', count: 1 }
      ],
      difficulty: 5,
      successRate: 0.4
    }
  },

  // 特殊丹药
  '回气丹': {
    type: RESOURCE_TYPES.PILL,
    name: '回气丹',
    description: '快速恢复气血的丹药，适合战斗中使用',
    rarity: 2,
    value: 100,
    usable: true,
    useEffect: {
      type: 'heal',
      amount: 50
    },
    recipe: {
      skill: '炼丹术',
      ingredients: [
        { item: '灵草', count: 2 }
      ],
      difficulty: 1,
      successRate: 0.8
    }
  },

  '灵元丹': {
    type: RESOURCE_TYPES.PILL,
    name: '灵元丹',
    description: '提升修为的丹药，可以增加修炼经验',
    rarity: 3,
    value: 300,
    usable: true,
    useEffect: {
      type: 'experience',
      amount: 50
    },
    recipe: {
      skill: '炼丹术',
      ingredients: [
        { item: '灵草', count: 3 },
        { item: '灵石', count: 1 }
      ],
      difficulty: 2,
      successRate: 0.6
    }
  },

  // 高级丹药
  '筑基丹': {
    type: RESOURCE_TYPES.PILL,
    name: '筑基丹',
    description: '筑基期修炼必备丹药，帮助筑基突破',
    rarity: 3,
    value: 800,
    usable: true,
    useEffect: {
      type: 'cultivation_boost',
      stage: '筑基'
    },
    recipe: {
      skill: '炼丹术',
      ingredients: [
        { item: '紫灵花', count: 3 },
        { item: '上品灵石', count: 2 }
      ],
      difficulty: 3,
      successRate: 0.6
    }
  },

  '金丹': {
    type: RESOURCE_TYPES.PILL,
    name: '金丹',
    description: '金丹期修炼丹药，凝聚金丹之力',
    rarity: 4,
    value: 1500,
    usable: true,
    useEffect: {
      type: 'cultivation_boost',
      stage: '金丹'
    }
  },

  '元婴丹': {
    type: RESOURCE_TYPES.PILL,
    name: '元婴丹',
    description: '元婴期修炼丹药，帮助凝练元婴',
    rarity: 5,
    value: 3000,
    usable: true,
    useEffect: {
      type: 'cultivation_boost',
      stage: '元婴'
    }
  },

  '化神丹': {
    type: RESOURCE_TYPES.PILL,
    name: '化神丹',
    description: '化神期修炼丹药，提升神魂修为',
    rarity: 6,
    value: 5000,
    usable: true,
    useEffect: {
      type: 'cultivation_boost',
      stage: '化神'
    }
  },

  '渡劫丹': {
    type: RESOURCE_TYPES.PILL,
    name: '渡劫丹',
    description: '渡劫期修炼丹药，帮助渡过天劫',
    rarity: 7,
    value: 8000,
    usable: true,
    useEffect: {
      type: 'cultivation_boost',
      stage: '渡劫'
    }
  },

  '仙丹': {
    type: RESOURCE_TYPES.PILL,
    name: '仙丹',
    description: '仙级丹药，蕴含天地之力',
    rarity: 8,
    value: 12000,
    usable: true,
    useEffect: {
      type: 'cultivation_boost',
      stage: '仙人'
    }
  },

  // 法宝 - 可装备的物品
  '飞剑': {
    type: RESOURCE_TYPES.ARTIFACT,
    name: '飞剑',
    description: '炼器成品，锋利无比，可御剑飞行',
    rarity: 3,
    value: 600,
    usable: true,
    useEffect: {
      type: 'equip',
      equipmentType: 'weapon',
      effects: { attack: 15 }
    },
    recipe: {
      skill: '炼器术',
      ingredients: [
        { item: '玄铁矿', count: 2 },
        { item: '狼牙', count: 3 },
        { item: '灵石', count: 3 }
      ],
      difficulty: 3,
      successRate: 0.6
    }
  },

  '护甲': {
    type: RESOURCE_TYPES.ARTIFACT,
    name: '护甲',
    description: '防御装备，蕴含灵力护体',
    rarity: 3,
    value: 500,
    usable: true,
    useEffect: {
      type: 'equip',
      equipmentType: 'armor',
      effects: { defense: 20 }
    },
    recipe: {
      skill: '炼器术',
      ingredients: [
        { item: '虎皮', count: 2 },
        { item: '玄铁矿', count: 3 },
        { item: '龙鳞', count: 1 }
      ],
      difficulty: 4,
      successRate: 0.5
    }
  },

  '灵符': {
    type: RESOURCE_TYPES.ARTIFACT,
    name: '灵符',
    description: '辅助法宝，可以提供各种增益效果',
    rarity: 2,
    value: 200,
    usable: true,
    useEffect: {
      type: 'talisman',
      effects: { mana: 20, divineSenseStrength: 10 }
    },
    recipe: {
      skill: '炼器术',
      ingredients: [
        { item: '紫灵花', count: 3 },
        { item: '雷晶', count: 1 },
        { item: '上品灵石', count: 2 }
      ],
      difficulty: 3,
      successRate: 0.7
    }
  },

  // 高级法宝
  '七星宝刀': {
    type: RESOURCE_TYPES.ARTIFACT,
    name: '七星宝刀',
    description: '七星炼制宝刀，刀光如七星璀璨，攻击力极强',
    rarity: 4,
    value: 2000,
    usable: true,
    useEffect: {
      type: 'equip',
      equipmentType: 'weapon',
      effects: { attack: 25 }
    },
    recipe: {
      skill: '炼器术',
      ingredients: [
        { item: '玄铁矿', count: 5 },
        { item: '灵石', count: 2 }
      ],
      difficulty: 4,
      successRate: 0.7
    }
  },

  '护心玉': {
    type: RESOURCE_TYPES.ARTIFACT,
    name: '护心玉',
    description: '护心灵玉，佩戴后可增强防御，抵御心魔侵袭',
    rarity: 3,
    value: 1200,
    usable: true,
    useEffect: {
      type: 'equip',
      equipmentType: 'accessory',
      effects: { defense: 15, mentalDefense: 20 }
    },
    recipe: {
      skill: '炼器术',
      ingredients: [
        { item: '玉石', count: 3 },
        { item: '灵草', count: 1 }
      ],
      difficulty: 3,
      successRate: 0.8
    }
  },

  // 其他特殊物品
  '禁地宝藏': {
    type: RESOURCE_TYPES.ARTIFACT,
    name: '禁地宝藏',
    description: '从禁地中获得的珍贵宝藏，蕴含神秘力量',
    rarity: 5,
    value: 2500,
    usable: false
  }
};

// 兼容性：保持旧的RESOURCES导出
export const RESOURCES = ITEMS;

// 状态效果定义
export const STATUS_EFFECTS = {
  '经脉受损': {
    name: '经脉受损',
    description: '战斗失败后经脉受损，影响修炼速度',
    type: 'debuff',
    effects: { cultivationSpeed: -30 },
    duration: -1, // 永久，除非治疗
    removeBy: ['续脉丹']
  },

  '聚气加速': {
    name: '聚气加速',
    description: '聚气丹效果，修炼速度提升',
    type: 'buff',
    effects: { cultivationSpeed: 20 },
    duration: 3600,
    icon: '⚡'
  },

  '重伤': {
    name: '重伤',
    description: '严重受伤，需要治疗',
    type: 'debuff',
    effects: { attack: -10 },
    duration: 7200,
    removeBy: ['疗伤丹', '续脉丹']
  }
};

// 资源获取来源
export const RESOURCE_SOURCES = {
  // 采集来源
  '木材': ['伐木采集'],
  '灵草': ['探索花园', '采摘灵花'],
  '九叶灵芝': ['采摘灵花', '探索遗迹'],
  '紫灵花': ['采摘灵花', '采集云露'],
  '血参': ['伐木采集', '追踪野兽'],
  '雪莲': ['攀登山巅', '穿越雷云'],

  // 战斗掉落
  '狼皮': ['灰狼'],
  '狼牙': ['灰狼'],
  '虎皮': ['猛虎'],
  '虎骨': ['猛虎'],
  '蛇胆': ['青蛇'],
  '龙鳞': ['云龙'],
  '凤羽': ['火凤'],

  // 矿物来源
  '灵石': ['探索遗迹', '挖矿'],
  '上品灵石': ['遗迹宝藏', '矿脉'],
  '玄铁矿': ['铁矿石'],
  '紫金矿': ['稀有矿脉'],

  // 灵物来源
  '云露': ['采集云露'],
  '火精': ['火灵'],
  '雷晶': ['雷云']
};

// 辅助函数
export function getResourceInfo(itemName) {
  return RESOURCES[itemName] || null;
}

export function getRecipeInfo(recipeName) {
  const item = ITEMS[recipeName];
  if (!item || !item.recipe) return null;

  // 返回兼容格式的配方信息
  return {
    name: item.name,
    description: item.description,
    ingredients: item.recipe.ingredients,
    difficulty: item.recipe.difficulty,
    successRate: item.recipe.successRate,
    skillRequired: item.recipe.skill,
    proficiencyGain: item.recipe.proficiencyGain || 1,
    refiningTime: item.recipe.refiningTime || 5000, // 默认5秒
    effects: item.useEffect
  };
}

export function canCraftRecipe(recipeName, inventory) {
  const item = ITEMS[recipeName];
  if (!item || !item.recipe) return false;

  return item.recipe.ingredients.every(ingredient => {
    const count = inventory[ingredient.item] || 0;
    return count >= ingredient.count;
  });
}

// 获取可炼制的丹药列表
export function getRefinablePills() {
  return Object.entries(ITEMS)
    .filter(([_, item]) => item.recipe && item.recipe.skill === '炼丹术')
    .map(([name, _]) => name);
}

// 获取可炼制的法宝列表
export function getRefinableTreasures() {
  return Object.entries(ITEMS)
    .filter(([_, item]) => item.recipe && item.recipe.skill === '炼器术')
    .map(([name, _]) => name);
}

export function getStatusEffect(effectName) {
  return STATUS_EFFECTS[effectName] || null;
}

export function getResourceSources(itemName) {
  return RESOURCE_SOURCES[itemName] || [];
}
